import { useCallback } from 'react';

import { CookieManager, dateTime, wholesaleValueConverter } from '@/_shared';

import {
  useCreateWholeSaleDiscountPriceMutation,
  useDeleteWholeSaleDiscountPriceMutation,
  useUpdateWholeSaleDiscountPriceMutation,
} from './wholesale-api';
import {
  modelWholesalePriceItem,
  validateWholesalePrice,
  wholesalePriceFormToUpdate,
  wholesalePriceQueryToForm,
} from './wholesale-prices';
import type { DeliverableWholesalePriceFormSchema } from './wholesale-prices/model';

import { useAgreementFormContext } from '../form-context';

type ArrayOfDiscounts = { startDate: string | null; wholeSaleDiscount: number | null }[];

export function useWholesaleDiscountPrice() {
  const { mutateAsync: updateWholesalePrices } = useUpdateWholeSaleDiscountPriceMutation();
  const { mutateAsync: deleteWholesalePrices } = useDeleteWholeSaleDiscountPriceMutation();
  const { mutateAsync: createWholesalePrices } = useCreateWholeSaleDiscountPriceMutation();

  const agreementForm = useAgreementFormContext();
  const [partner, agreementId, parent_id] = agreementForm.watch(['partner', 'id', 'parent_id']);
  const isAddendum = !!parent_id;

  const storeStartDateAndDiscount = useCallback(
    (arrayOfDiscounts: ArrayOfDiscounts) => {
      const agreementsData = CookieManager.getAgreementDeliverablesState() as Record<number, ArrayOfDiscounts>;

      if (agreementsData && agreementsData[agreementId]) {
        agreementsData[agreementId] = arrayOfDiscounts;
        CookieManager.setAgreementDeliverablesState(JSON.stringify(agreementsData));
      } else {
        CookieManager.setAgreementDeliverablesState(JSON.stringify({ [agreementId]: arrayOfDiscounts }));
      }
    },
    [agreementId]
  );

  const handleStoreStartDateAndDiscount = useCallback(
    (deliverable_wholesale_price: DeliverableWholesalePriceFormSchema[]) => {
      if (deliverable_wholesale_price.length > 0) {
        const arrayOfDiscounts = deliverable_wholesale_price.map((t) => {
          const { start_date, wholesale_discount } = t;

          let startDate = start_date;
          if (!startDate?.endsWith?.('00:00:00')) {
            startDate = startDate + ' 00:00:00';
          }

          return {
            startDate: dateTime.isValid(startDate || '') ? startDate : null,
            wholeSaleDiscount: wholesale_discount ? Number(wholesale_discount?.toFixed(4)) : null,
          };
        });

        storeStartDateAndDiscount(arrayOfDiscounts);
      }
    },
    [storeStartDateAndDiscount]
  );

  const getStoredStartDateAndDiscount = useCallback((): ArrayOfDiscounts | null => {
    const agreementsData = CookieManager.getAgreementDeliverablesState();
    return agreementsData ? agreementsData[agreementId] : null;
  }, [agreementId]);

  const getNewDiscountItem = useCallback(
    (price: number, currentItems: DeliverableWholesalePriceFormSchema[] = []): typeof modelWholesalePriceItem => {
      const indexNumber = currentItems.length;
      const storedStartDateAndDiscounts = getStoredStartDateAndDiscount();
      const singleDiscount = storedStartDateAndDiscounts?.[indexNumber] ?? null;

      let calcWholesaleDiscount = singleDiscount?.wholeSaleDiscount ?? null;
      if (indexNumber > 0) {
        const previousDiscount = currentItems[indexNumber - 1].wholesale_discount;
        if (previousDiscount) {
          calcWholesaleDiscount = previousDiscount;
        }
      }

      const calcWholesalePrice = wholesaleValueConverter.getWholesalePrice(Number(price), calcWholesaleDiscount);
      const startDate = singleDiscount?.startDate ?? null;

      const newItem = {
        ...modelWholesalePriceItem,
        start_date: startDate,
        wholesale_discount: calcWholesaleDiscount,
        wholesale_price: calcWholesalePrice || null,
      };

      return newItem;
    },
    [getStoredStartDateAndDiscount]
  );

  // match and find what data is missing from deliverable_wholesale_price and data and make an array of it
  const findCategoriesOfData = useCallback(
    (dwsp: DeliverableWholesalePriceFormSchema[], originalItems: DeliverableWholesalePriceFormSchema[] = []) => {
      // const originalItems = data?.deliverable_wholesale_price ?? [];

      const deletedItems = originalItems.filter((originalItem) => {
        if (originalItem.agreement_deliverable_id === -1) return false;
        return !dwsp.some((currentItem) => currentItem.id === originalItem.id);
      });

      // Find items that exist in deliverable_wholesale_price but are missing from data (new items)
      const newItems = dwsp.filter((currentItem) => {
        return currentItem.agreement_deliverable_id === -1;
      });

      const updatedItems = dwsp.filter((item) => {
        const originalItem = originalItems?.find((d) => d.id === item.id);

        // console.log('item', { item, originalItem });
        // find those items which are not in data
        if (!originalItem) return false;

        return (
          item.start_date !== originalItem.start_date ||
          item.end_date !== originalItem.end_date ||
          item.wholesale_price !== originalItem.wholesale_price ||
          item.wholesale_discount !== originalItem.wholesale_discount
        );
      });

      const dataToReturn = dwsp.filter((item) => {
        // Skip items that are new (agreement_deliverable_id === -1)
        if (item.agreement_deliverable_id === -1) return false;

        // Skip items that are in deletedItems
        if (deletedItems.some((deletedItem) => deletedItem.id === item.id)) return false;

        // Skip items that are in newItems
        if (newItems.some((newItem) => newItem.id === item.id)) return false;

        return true;
      });

      return {
        deletedItems,
        newItems,
        updatedItems,
        dataToReturn,
      };
    },
    []
  );

  const onUpdateWholesalePrices = useCallback(
    async (
      deliverable_wholesale_price: DeliverableWholesalePriceFormSchema[],
      originalItems: DeliverableWholesalePriceFormSchema[] = [],
      deliverable_id: number,
      lockdownOverridePin?: string,
      overrideJustificationNote?: string
    ) => {
      const { deletedItems, updatedItems, newItems, dataToReturn } = findCategoriesOfData(
        deliverable_wholesale_price,
        originalItems
      );

      const deletePayload = deletedItems.map((item) => item.id);

      if (deletePayload.length > 0) {
        await deleteWholesalePrices({
          items: deletePayload,
          lockdown_override_pin: lockdownOverridePin,
          override_justification_note: overrideJustificationNote,
        });
      }

      const updatePayload = updatedItems.map((item) => ({
        ...item,
      }));

      if (updatePayload.length > 0) {
        await updateWholesalePrices({
          items: wholesalePriceFormToUpdate(updatePayload, deliverable_id),
          lockdown_override_pin: lockdownOverridePin,
          override_justification_note: overrideJustificationNote,
        });
      }

      const createPayload = newItems.map((item) => ({
        ...item,
        agreement_deliverable_id: deliverable_id,
      }));

      let nDataToReturn = [...dataToReturn];
      if (createPayload.length > 0) {
        const res = await createWholesalePrices({
          items: wholesalePriceFormToUpdate(createPayload, deliverable_id),
          lockdown_override_pin: lockdownOverridePin,
          override_justification_note: overrideJustificationNote,
        });
        if (res) {
          nDataToReturn = [...nDataToReturn, ...wholesalePriceQueryToForm(res)];
        }
      }

      handleStoreStartDateAndDiscount(nDataToReturn);

      return nDataToReturn;
    },
    [
      createWholesalePrices,
      deleteWholesalePrices,
      findCategoriesOfData,
      handleStoreStartDateAndDiscount,
      updateWholesalePrices,
    ]
  );

  return {
    isAddendum,
    canAddWholesalePrice: !!partner,
    getStoredStartDateAndDiscount,
    getNewDiscountItem,
    handleStoreStartDateAndDiscount,
    handleScheduleValidation: validateWholesalePrice,

    onUpdateWholesalePrices,
  };
}

export type UseWholesaleDiscountPriceReturn = ReturnType<typeof useWholesaleDiscountPrice>;
